import * as React from "react";
import { ClipboardPaste, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

export const CODE_LENGTH = 4;

function normalize(raw: string, length: number) {
  return raw.replace(/[^0-9]/g, "").slice(0, length);
}

/** Plockar ut en kod ur text man kopierat, t.ex. hela raden ur mejlet. */
function extractCode(raw: string, length: number): string | null {
  const digits = raw.replace(/[^0-9]/g, "");
  if (digits.length === length) return digits;
  const match = raw.match(new RegExp(`(?<!\\d)\\d{${length}}(?!\\d)`));
  return match ? match[0] : null;
}

export interface OtpCodeFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Körs så fort koden är komplett (via inskrivning, autofyll eller inklistring). */
  onComplete: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Visa knappen som klistrar in koden från urklipp. */
  readClipboard?: boolean;
  busy?: boolean;
  busyLabel?: string;
  invalid?: boolean;
  label?: string;
  className?: string;
}

/**
 * Fyrsiffrigt fält för engångskoder från mejlet. Stöder autofyll
 * (autocomplete="one-time-code" – iOS föreslår koden ovanför tangentbordet),
 * inklistring och skickar in koden automatiskt när den är komplett. Urklipp
 * läses tyst när man kommer tillbaka till fliken, och annars via knappen –
 * Safari nekar all urklippsläsning utan en riktig knapptryckning.
 */
export function OtpCodeField({
  value,
  onChange,
  onComplete,
  length = CODE_LENGTH,
  disabled = false,
  autoFocus = false,
  readClipboard = true,
  busy = false,
  busyLabel = "Kollar koden…",
  invalid = false,
  label = "Kod från mejlet",
  className,
}: OtpCodeFieldProps) {
  const [pasteError, setPasteError] = React.useState(false);
  const [focused, setFocused] = React.useState(false);

  const canPaste = typeof navigator !== "undefined" && Boolean(navigator.clipboard?.readText);

  const pasteFromClipboard = React.useCallback(async () => {
    setPasteError(false);
    try {
      const text = await navigator.clipboard.readText();
      const next = extractCode(text, length);
      if (!next) {
        setPasteError(true);
        return;
      }
      onChange(next);
      if (!disabled) window.setTimeout(() => onComplete(next), 250);
    } catch {
      setPasteError(true);
    }
  }, [disabled, length, onChange, onComplete]);

  // Tyst försök att läsa urklipp när man kommer tillbaka från mejlappen.
  // Fungerar där behörigheten är beviljad; i Safari händer ingenting och
  // användaren kan trycka på knappen i stället.
  React.useEffect(() => {
    if (!readClipboard || disabled || value.length >= length) return;
    if (typeof navigator === "undefined" || !navigator.clipboard?.readText) return;
    let cancelled = false;

    const tryRead = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const permissions = (
          navigator as Navigator & {
            permissions?: { query: (d: { name: PermissionName }) => Promise<PermissionStatus> };
          }
        ).permissions;
        if (permissions?.query) {
          const status = await permissions.query({
            name: "clipboard-read" as PermissionName,
          });
          if (status.state !== "granted") return;
        }
        const text = await navigator.clipboard.readText();
        const next = extractCode(text, length);
        if (cancelled || !next) return;
        onChange(next);
        window.setTimeout(() => {
          if (!cancelled) onComplete(next);
        }, 250);
      } catch {
        /* tyst – användaren kan använda knappen */
      }
    };

    void tryRead();
    window.addEventListener("focus", tryRead);
    document.addEventListener("visibilitychange", tryRead);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", tryRead);
      document.removeEventListener("visibilitychange", tryRead);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readClipboard, disabled, length]);

  const inputs = React.useRef<(HTMLInputElement | null)[]>([]);

  const focusBox = (i: number) => {
    const el = inputs.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  /** Skriver in tecken från och med ruta `index`. */
  const putAt = (index: number, raw: string) => {
    const digits = raw.replace(/[^0-9]/g, "");
    if (!digits) return;
    const chars = value.padEnd(length, " ").split("");
    let i = index;
    for (const d of digits) {
      if (i >= length) break;
      chars[i] = d;
      i += 1;
    }
    const next = chars.join("").replace(/ /g, "").slice(0, length);
    onChange(next);
    if (next.length === length && !disabled) {
      onComplete(next);
      focusBox(length - 1);
    } else {
      focusBox(i);
    }
  };

  const removeAt = (index: number) => {
    const chars = value.split("");
    chars.splice(index, 1);
    onChange(chars.join(""));
  };

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      {/* Fyra riktiga fält. Det första bär autocomplete="one-time-code" så att
          iOS/Android föreslår koden ovanför tangentbordet; autofyll av hela
          koden fördelas ut över rutorna. */}
      <div className="flex w-full max-w-[15rem] items-center justify-center gap-2">
        {Array.from({ length }).map((_, i) => {
          const char = value[i] ?? "";
          return (
            <input
              key={i}
              ref={(el) => {
                inputs.current[i] = el;
              }}
              autoFocus={autoFocus && i === 0}
              type="text"
              {...(i === 0
                ? { name: "one-time-code", id: "one-time-code", autoComplete: "one-time-code" }
                : { autoComplete: "off" })}
              inputMode="numeric"
              pattern="[0-9]*"
              autoCapitalize="off"
              spellCheck={false}
              disabled={disabled}
              value={char}
              aria-label={`${label} – siffra ${i + 1}`}
              onFocus={(e) => {
                setFocused(true);
                e.currentTarget.select();
              }}
              onBlur={() => setFocused(false)}
              onChange={(e) => putAt(i, e.target.value)}
              onPaste={(e) => {
                e.preventDefault();
                const text = e.clipboardData.getData("text");
                const found = extractCode(text, length) ?? normalize(text, length);
                if (!found) return;
                onChange(found);
                if (found.length === length && !disabled) {
                  onComplete(found);
                  focusBox(length - 1);
                } else {
                  focusBox(found.length);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace") {
                  e.preventDefault();
                  if (value[i]) {
                    removeAt(i);
                    focusBox(i);
                  } else if (i > 0) {
                    removeAt(i - 1);
                    focusBox(i - 1);
                  }
                } else if (e.key === "ArrowLeft") {
                  e.preventDefault();
                  focusBox(i - 1);
                } else if (e.key === "ArrowRight") {
                  e.preventDefault();
                  focusBox(i + 1);
                } else if (e.key === "Home") {
                  e.preventDefault();
                  focusBox(0);
                } else if (e.key === "End") {
                  e.preventDefault();
                  focusBox(length - 1);
                } else if (e.key === "Delete") {
                  e.preventDefault();
                  if (value[i]) removeAt(i);
                }
              }}
              className={cn(
                "h-14 w-full min-w-0 flex-1 rounded-xl border border-input bg-background text-center font-mono text-2xl tabular-nums text-foreground caret-primary outline-none transition-colors",
                char && "border-primary/60",
                focused && !disabled && "focus:border-primary focus:ring-2 focus:ring-primary/30",
                value.length === length && !invalid && "border-emerald-500/70",
                invalid && "border-destructive text-destructive",
                disabled && "opacity-50",
              )}
            />
          );
        })}
      </div>

      {busy ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {busyLabel}
        </p>
      ) : readClipboard && canPaste && value.length < length ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => void pasteFromClipboard()}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline-offset-4 hover:underline disabled:opacity-50"
        >
          <ClipboardPaste className="h-3.5 w-3.5" aria-hidden="true" />
          Klistra in kod
        </button>
      ) : null}

      {pasteError && (
        <p className="text-xs text-muted-foreground">
          Vi hittade ingen kod i urklippet – kopiera koden i mejlet och försök igen.
        </p>
      )}
    </div>
  );
}
